import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import { FaInstagram, FaPinterestP, FaLinkedinIn } from "react-icons/fa";
import { SiSubstack } from "react-icons/si";
import { MarketingHeader } from "@/components/marketing-header";
import { Footer } from "@/components/footer";
import { Layout } from "@/components/layout";
import { apiUrl } from "@/lib/api-base";
import { useAuth } from "@/hooks/useAuth";
import { useSeo, SEO_PAGES } from "@/hooks/use-seo";

const NOTIFICATION_EMAIL = "unfoldformomentum@gmail.com";

const TOPICS = [
  "Feedback & ideas",
  "Partnership or collaboration",
  "Press & media",
  "Billing or account help",
  "Something else",
];

const SOCIALS = [
  { label: "Instagram", href: "https://instagram.com/unfoldwin", Icon: FaInstagram },
  { label: "Pinterest", href: "https://pinterest.com/unfoldwin", Icon: FaPinterestP },
  { label: "LinkedIn", href: "https://linkedin.com/company/unfoldwin", Icon: FaLinkedinIn },
  { label: "Substack", href: "https://unfoldwin.substack.com", Icon: SiSubstack },
];

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export default function ContactPage() {
  useSeo(SEO_PAGES["/contact"]);
  const { isAuthenticated } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState(TOPICS[0]);
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const normalizedEmail = email.trim().toLowerCase();
    if (!isValidEmail(normalizedEmail)) {
      setEmailError("Please enter a valid email address for the contact form.");
      return;
    }
    setEmailError(null);
    setSubmitting(true);

    try {
      const payload = {
        name: name.trim(),
        email: normalizedEmail,
        topic: topic.trim(),
        message: message.trim(),
      };

      const res = await fetch(apiUrl("/contact"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || "Unable to submit your message right now.");
      }

      setSubmitted(true);
    } catch (err) {
      const messageText =
        err instanceof Error ? err.message : "Unable to submit your message right now.";
      setSubmitError(messageText);
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <main className="flex-1">
        <section className="relative overflow-hidden py-16 md:py-20">
          <div
            className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full pointer-events-none opacity-70"
            style={{
              background:
                "radial-gradient(ellipse, hsl(355 70% 88% / 0.5), hsl(258 45% 82% / 0.25), transparent 70%)",
              transform: "translate(25%, -25%)",
            }}
          />
          <div className="max-w-3xl mx-auto px-5 text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest mb-6">
                <Mail className="w-3.5 h-3.5" />
                Get in touch
              </span>
              <h1 className="font-display text-4xl md:text-5xl font-semibold leading-[1.1] text-foreground mb-4">
                We'd love to <span className="text-primary">hear from you.</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-xl mx-auto">
                Questions, ideas, partnerships, or just a story about how reflection changed your week — our inbox is open.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="pb-16 md:pb-24">
          <div className="max-w-2xl mx-auto px-5">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="rounded-3xl p-6 md:p-8 bg-white/70 border border-white/60 shadow-sm backdrop-blur-sm"
            >
              {submitted ? (
                <div className="py-10 text-center">
                  <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-7 h-7" />
                  </div>
                  <h2 className="font-display text-2xl font-medium text-foreground mb-2">
                    Sent
                  </h2>
                  <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-6">
                    Your message has been sent to the team. If you need a faster reply, write us at{" "}
                    <a
                      href={`mailto:${NOTIFICATION_EMAIL}`}
                      className="text-primary underline underline-offset-2"
                    >
                      {NOTIFICATION_EMAIL}
                    </a>
                    .
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSubmitted(false);
                      setName("");
                      setEmail("");
                      setTopic(TOPICS[0]);
                      setMessage("");
                    }}
                    className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Your name" htmlFor="contact-name">
                      <input
                        id="contact-name"
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Maya Patel"
                        className="w-full h-11 px-4 rounded-2xl bg-background/80 border border-border/60 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition"
                      />
                    </Field>
                    <Field label="Email" htmlFor="contact-email">
                      <input
                        id="contact-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (emailError) setEmailError(null);
                        }}
                        onBlur={() => {
                          const normalizedEmail = email.trim().toLowerCase();
                          if (!normalizedEmail) return;
                          if (!isValidEmail(normalizedEmail)) {
                            setEmailError("Please enter a valid email address for the contact form.");
                          }
                        }}
                        aria-invalid={Boolean(emailError)}
                        aria-describedby={emailError ? "contact-email-error" : undefined}
                        placeholder="you@example.com"
                        className={`w-full h-11 px-4 rounded-2xl bg-background/80 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 transition ${
                          emailError
                            ? "border border-red-500/70 focus:ring-red-500/20 focus:border-red-500"
                            : "border border-border/60 focus:ring-primary/30 focus:border-primary/40"
                        }`}
                      />
                      {emailError && (
                        <p id="contact-email-error" className="mt-1.5 text-xs text-red-600">
                          {emailError}
                        </p>
                      )}
                    </Field>
                  </div>

                  <Field label="What's this about?" htmlFor="contact-topic">
                    <select
                      id="contact-topic"
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      className="w-full h-11 px-4 rounded-2xl bg-background/80 border border-border/60 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition"
                    >
                      {TOPICS.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Your message" htmlFor="contact-message">
                    <textarea
                      id="contact-message"
                      required
                      rows={6}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Tell us what's on your mind…"
                      className="w-full px-4 py-3 rounded-2xl bg-background/80 border border-border/60 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition resize-y"
                    />
                  </Field>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-2">
                    <p className="text-xs text-muted-foreground">
                      We usually reply within 2 business days.
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-primary text-white text-sm font-medium shadow-sm hover:bg-primary/90 hover:-translate-y-0.5 transition-all"
                    >
                      {submitting ? "Sending..." : "Send message"} <Send className="w-4 h-4" />
                    </button>
                  </div>
                  {submitError && (
                    <p className="text-xs text-red-600">{submitError}</p>
                  )}
                </form>
              )}
            </motion.div>

            <div className="mt-10 rounded-3xl p-6 md:p-8 text-center border border-border/40 bg-muted/30">
              <h2 className="font-display text-xl md:text-2xl font-medium text-foreground mb-2">
                Or follow the journey
              </h2>
              <p className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">
                Small reflections, daily prompts, and product notes — pick the place you already spend time.
              </p>
              <div className="flex items-center justify-center gap-3">
                {SOCIALS.map(({ label, href, Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="w-11 h-11 rounded-full flex items-center justify-center bg-white text-foreground/70 border border-border/40 hover:bg-primary hover:text-white hover:border-primary transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
  );

  if (isAuthenticated) {
    return <Layout>{content}</Layout>;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <MarketingHeader />
      {content}
      <Footer />
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="text-xs font-semibold uppercase tracking-widest text-foreground/70 mb-1.5 block">
        {label}
      </span>
      {children}
    </label>
  );
}
